import { IMessagingService } from "../services";
import { getCookie, setCookie } from "../utils";
import { Component } from "./component";

interface IFocusableElement {
    focus(): void;
}

function isFocusable(element: any): element is IFocusableElement {
    return (typeof element.focus === "function");
}

function getKeyboardFocusableElements(element) {
    return [...element.querySelectorAll(
        'a[href], button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'
    )].filter(el => !el.hasAttribute('disabled') && !el.getAttribute("aria-hidden"));
}

function getVisibleFocusableElements(elements: Array<HTMLElement>): Array<HTMLElement> {
    return elements.filter(
        el => el.offsetWidth > 0 && el.offsetHeight > 0);
}

const calculateScrollbarWidth = function () {
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);
    const inner = document.createElement('div');
    outer.appendChild(inner);
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    document.body.removeChild(outer);
    return scrollbarWidth;
};

class ModalComponent extends Component {
    protected showAgain: boolean;
    protected modal: HTMLElement;
    protected backdrop: HTMLElement;
    protected header: HTMLDivElement;
    protected isCookieModal: boolean;
    protected firstFocusTargetEl: HTMLElement;

    private id: string;
    private isShowing: boolean;
    private onKeydown: EventListener;
    private onClick: EventListener;
    private focusableEls: any[];
    private priorFocusedEl: any;
    private onResize: EventListener;
    private scrollbarWidth: number;
    private originalBodyPad: string;
    private bodyIsOverflowing: boolean;

    constructor(componentEl: HTMLElement, messagingService: IMessagingService) {
        super(componentEl, messagingService);

        this.isCookieModal = false;
        this.showAgain = (componentEl.getAttribute("data-show-again") ?? "false").toLowerCase() === "true";
        this.id = componentEl.dataset.id;
        this.modal = componentEl;
        this.header = this.modal.querySelector<HTMLDivElement>('.modal__header');

        this.init();
    }

    protected async init() {

        // Check modal should be shown before continuing
        if (!this.showAgain && !this.isCookieModal) {
            const cookie = getCookie("modal-" + this.id);
            if (cookie) {
                return;
            }
        }

        this.backdrop = null;
        this.isShowing = false;
        this.bodyIsOverflowing = null;
        this.originalBodyPad = null;
        this.scrollbarWidth = calculateScrollbarWidth();

        // Events
        this.onKeydown = this.onKeydownHandler.bind(this);
        this.onClick = this.onClickHandler.bind(this);
        this.onResize = this.onResizeHandler.bind(this);

        // Get prior focused element
        this.priorFocusedEl = document.activeElement;

        if (!this.isCookieModal) { this.showModal() };
    }

    protected UpdateFocusableElements() {
        //made this a protected method, as derived classes may want to retrigger it (when injecting content from ajax calls for instance)

        // Get focusable elements
        this.focusableEls = getKeyboardFocusableElements(this.modal);

        // Get focus target element
        this.firstFocusTargetEl = null;
        this.modal.querySelectorAll<HTMLElement>('*[tabindex="-1"], .cookiemodal__back').forEach(el => {
            //just get the first control that is actually visible
            if (this.firstFocusTargetEl == null && el.offsetWidth > 0 && el.offsetHeight > 0) {
                this.firstFocusTargetEl = el;
            }
        });
        //this.firstFocusTargetEl = this.modal.querySelector<HTMLElement>('*[tabindex="-1"]');

    }

    private onClickHandler(e) {
        let el = e.target;
        let allEls = [e.target];
        while (el.parentNode) {
            el = el.parentNode;
            allEls.push(el);
        }
        allEls.every((element) => {
            if (element.tagName === "HTML" || element.classList.contains("modal__content")) {
                return false;
            }
            if ((element.getAttribute("data-dismiss") === "modal" || element.classList.contains("modal")) && this.isCookieModal == false) {
                this.hideModal();
                return false;
            }
            return true;
        });
    }

    protected onKeydownHandler(e) {
        if (!this.modal.contains(e.target)) {
            //when multiple modals are shown or the cookie modal is on top of another modal the clicks bubble to every modal, even if they are not meant to receive them, so ignore any for targets that are not in this modal
            return;
        }
        if (e.which === 27 && this.isCookieModal == false) {
            this.hideModal();
        } else if (e.which === 9) {
            if (!this.focusableEls.includes(e.target) && e.target !== this.firstFocusTargetEl) {
                this.firstFocusTargetEl.focus();
            }
            var visibleElements: Array<HTMLElement> = getVisibleFocusableElements(this.focusableEls);
            if (!e.shiftKey) {
                if (e.target === visibleElements[visibleElements.length - 1]) {
                    visibleElements[0].focus();
                    e.preventDefault();
                }
            } else {
                if (e.target === visibleElements[0]) {
                    visibleElements[visibleElements.length - 1].focus();
                    e.preventDefault();
                }
            }
        }
    }

    private resize() {
        var modalIsOverflowing = this.modal.scrollHeight > document.documentElement.clientHeight;
        this.modal.style.paddingLeft = !this.bodyIsOverflowing && modalIsOverflowing ? this.scrollbarWidth + "px" : "";
        this.modal.style.paddingRight = this.bodyIsOverflowing && !modalIsOverflowing ? this.scrollbarWidth + "px" : "";
    }

    private backdropDisplay() {
        this.backdrop = document.createElement("div");
        this.backdrop.classList.add('modal-backdrop', 'modal-fade');
        document.body.appendChild(this.backdrop);
        this.backdrop.offsetWidth;
        this.backdrop.classList.add('modal-show');
    }

    private onResizeHandler() {
        this.resize();
    }

    public showModal() {
        this.checkScrollbar();
        this.setScrollbar();

        document.body.classList.add("modal-open");

        this.modal.style.display = "flex";
        this.modal.scrollTop = 0;

        document.body.addEventListener("keydown", this.onKeydown);
        this.modal.addEventListener("click", this.onClick);
        window.addEventListener("resize", this.onResize);

        this.modal.offsetWidth;
        this.modal.classList.add('modal-show');

        this.UpdateFocusableElements();

        setTimeout(() => {
            this.isShowing = true;
            if (this.firstFocusTargetEl) {
                this.firstFocusTargetEl.focus();
            }
        }, 200);
        this.backdropDisplay();
        this.resize();

        return this;
    }

    public hideModal() {

        this.modal.classList.remove('modal-show');
        this.modal.classList.remove('modal-initially-shown');
        if (this.backdrop != null) { //the cookie modal hides in the background without a visible backdrop
            this.backdrop.classList.remove('modal-show');
        }


        // Remove events
        document.body.removeEventListener("keydown", this.onKeydown);
        this.modal.removeEventListener("click", this.onClick);
        window.removeEventListener("resize", this.onResize);

        if (!this.modal.parentElement.querySelector('.modal-show')) {
            setTimeout(() => {
                document.body.classList.remove("modal-open");
                document.body.style.paddingRight = this.originalBodyPad;
            }, 100);
        }

        setTimeout(() => {
            if (this.backdrop != null) {
                this.backdrop.parentNode.removeChild(this.backdrop);
            }
            this.modal.style.display = "none";
            this.isShowing = false;

            //remove earlier in the process so that the list-child css makes any lessor modal visible
            if (!this.isCookieModal) {
                this.modal.parentNode.removeChild(this.modal);
            }

            //check to see if there are any other modals in the hierarchy that should receive the focus
            var modalNumber = parseInt(this.modal.getAttribute('data-instanceid'));
            if (modalNumber > 1) {
                //see if the lessor modal is visible
                var lessorModal: HTMLDivElement = document.body.querySelector(`.modal[data-instanceid="${modalNumber - 1}"]`);
                if (lessorModal != null && lessorModal.offsetWidth > 0 && lessorModal.offsetHeight > 0) {
                    //yes, the lessorModal is visible
                    var firstFocusElement: HTMLElement = lessorModal.querySelector<HTMLElement>('*[tabindex="-1"]');
                    if (firstFocusElement != null) {
                        firstFocusElement.focus();
                    }
                }
            }
            else if (this.priorFocusedEl.tagName === "BODY" || this.priorFocusedEl.tagName === "MAIN") {
                if (!document.body.getAttribute("tabindex")) {
                    document.body.setAttribute("tabindex", "-1");
                    document.body.focus();
                    document.body.removeAttribute("tabindex");
                } else {
                    document.body.focus();
                }
            } else if (this.priorFocusedEl && isFocusable(this.priorFocusedEl)) {
                this.priorFocusedEl.focus();
            }
            if (!this.showAgain && !this.isCookieModal) {
                setCookie("modal-" + this.id, "true", 365);
            }
        }, 200);

        return this;
    }

    private checkScrollbar() {
        this.bodyIsOverflowing = document.body.clientWidth < window.innerWidth;
    }

    private setScrollbar() {
        this.originalBodyPad = document.body.style.paddingRight || "";
        if (this.bodyIsOverflowing) {
            let basePadding = parseInt(this.originalBodyPad || "0", 10);
            document.body.style.paddingRight = basePadding + this.scrollbarWidth + "px";
        }
    }

}

export { ModalComponent };
